import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {BrokerService} from '../../../services/broker.service';
import {SharedService} from '../../../../common/services/shared-service';
import {LookupService} from '../../../../common/services/lookup.service';
import {BrokerDTO} from '../../../models/broker/BrokerDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {AddressFormComponent} from '../../../../common/components/common-steps/address-form/address-form.component';
import {ContactFormComponent} from '../../../../common/components/common-steps/contact-form/contact-form.component';
import {AddressesArrayDTO} from '../../../../ent/models/AddressesArrayDTO';
import {ContactArrayDTO} from '../../../../ent/models/ContactsArrayDTO';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';
import {MatIconType} from '../../../../common/types/icon.enum';
import {HelpInfo} from '../../../../clm/types/help-info.enum';
import {MatDialog} from '@angular/material/dialog';
import {
  UnderwritingConcession,
  UnderwritingConcessionDialogComponent,
  UnderwritingConcessionDialogData
} from '../../../../common/dialogs/underwriting-concession-dialog/underwriting-concession-dialog.component';
import {
  BrokerPermissionDialogData,
  ManageBrokerPermissionsDialogComponent,
} from '../../../../common/dialogs/manage-broker-permissions-dialog/manage-broker-permissions-dialog.component';
import {
  PolicyholderGroupUnderwritingTypeLinkDTO
} from '../../../../ent/models/PolicyholderGroupUnderwritingTypeLinkDTO';
import {UtilService} from '../../../../common/services/util.service';
import {EnterpriseRoutesQueryParam} from '../../../../common/types/routes-query-param.enum';
import {PortalType} from '../../../../common/types/portal.enum';
import {BrokerFeatureDTO} from '../../../../pbm/models/BrokerFeatureDTO';
import {AccessLevel} from '../../../../common/types/feature';

enum RouteHeadings {
  AddBroker = 'Add Broker',
  EditBroker = 'Edit Broker',
}

@Component({
  selector: 'app-add-edit-broker',
  templateUrl: './add-edit-broker.component.html',
  styleUrls: [
    '../../../../../../styles/global/_add-view-edit-styles.scss'
  ]
})
export class AddEditBrokerComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly DisplayIcon = MatIconType;

  generalFormGroup: FormGroup;
  statutoryFormGroup: FormGroup;
  tabName: string;
  backPageName: string;
  isEditMode = false;
  brokerId: number;
  customRoute: string;
  canManageFeatures = false;
  numberOfAllowedFeatures = 0;

  heading: string;
  businessTypes: NameValueDTO[] = [];
  entityStatuses: NameValueDTO[] = [];
  brokerGroups: NameValueDTO[] = [];
  underwritingConcessionsType: NameValueDTO[] = [];
  availableFeatures: NameValueCodeDTO[] = [];
  selectedFeatures: BrokerFeatureDTO[] = [];
  selectedAddresses: AddressesArrayDTO[] = [];
  selectedContacts: ContactArrayDTO[] = [];
  selectedBroker: BrokerDTO;

  @ViewChild('addressFormComponent') addressFormComponent: AddressFormComponent;
  @ViewChild('contactFormComponent') contactFormComponent: ContactFormComponent;
  protected underwritingConcessions: PolicyholderGroupUnderwritingTypeLinkDTO[] = [];
  private loggedInUserName: string;
  private addressPayload: { addresses: AddressesArrayDTO[] };
  private contactPayload: { contacts: ContactArrayDTO[] };
  private currentPortal: PortalType;

  constructor(
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private utilService: UtilService,
    private brokerService: BrokerService,
    private sharedService: SharedService,
    private lookupService: LookupService,
    private userAuthorisationService: UserAuthorisationService,
  ) {
    const parentData = this.route.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || PortalType.Normal;
    this.customRoute = this.getCustomRoute();
    this.tabName = EnterpriseRoutesQueryParam.Brokers;
    this.backPageName = 'enterprise-management';
    this.getHelpInfo(HelpInfo.Broker);
  }

  ngOnInit(): void {
    this.initForms();
    this.initLookUpValues();
    this.initSubscriptions();

    this.canManageFeatures = this.userAuthorisationService.hasRoleEnterpriseTeamLeader();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions() {
    this.sharedService
      .getLoggedInProfile$()
      .pipe(take(1))
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });

    this.route.params.subscribe(params => {
      this.brokerId = params['id'];
      this.isEditMode = !!this.brokerId;

      if (this.isEditMode) {
        this.getBrokerByBrokerId(this.brokerId);
      }
    });

    this.onInputUpdatePageHeading('');
  }


  getHelpInfo(componentName: string) {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  resetGeneralFormGroup() {
    this.generalFormGroup.reset();
  }

  resetStatutoryFormGroup() {
    this.statutoryFormGroup.reset();
  }

  isFinishButtonDisabled(): boolean {
    return !this.isAllowedToSubmit() ||
      !this.generalFormGroup.valid ||
      !this.statutoryFormGroup.valid ||
      this.addressFormComponent?.selectedAddresses.length === 0 ||
      this.contactFormComponent?.selectedContacts.length === 0;
  }

  isAllowedToSubmit(): boolean {
    let hasAccessToSubmitPolicyholderGroup = false;
    const hasRoleEnterpriseManager = this.userAuthorisationService.hasRoleEnterpriseManager();
    const hasRoleOnboardingManager = this.userAuthorisationService.hasOnBoardingManager();

    if (hasRoleEnterpriseManager) {
      hasAccessToSubmitPolicyholderGroup = true;
    }
    else if (hasRoleOnboardingManager) {
      hasAccessToSubmitPolicyholderGroup = true;
    }
    return hasAccessToSubmitPolicyholderGroup;
  }

  onSubmit(): void {
    if (this.generalFormGroup.valid && this.statutoryFormGroup.valid &&
      this.addressFormComponent.selectedAddresses.length > 0 &&
      this.contactFormComponent.selectedContacts.length > 0) {

      const brokerData: BrokerDTO = this.getBrokerDTOPayload();

      this.brokerService.saveBroker(brokerData)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response: BrokerDTO) => {
            this.sharedService.openSnackBar(`Broker ${response.name} saved successfully`);
            this.navigateToBrokerPageBasedOnPortal();
          },
          error: (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        });
    }
  }

  initForms(): void {
    this.generalFormGroup = this.formBuilder.group({
      name: new FormControl('', Validators.required),
      brokerNumber: new FormControl('', Validators.required),
      startDate: new FormControl(null, Validators.required),
      endDate: new FormControl(null),
      businessType: new FormControl('', Validators.required),
      entityStatus: new FormControl('', Validators.required),
      brokerGroup: new FormControl(''),
    });

    this.statutoryFormGroup = this.formBuilder.group({
      businessLicenseNumber: new FormControl('', Validators.required),
      businessLicenseExpiryDate: new FormControl(null, Validators.required),
      vatNumber: new FormControl(''),
      vatOverride: new FormControl(false),
      gstNumber: new FormControl(''),
      gstOverride: new FormControl(false),
      sanctionCheckDate: new FormControl(null),
      sanctionReference: new FormControl(''),
      sanctionIndicator: new FormControl(false)
    });
  }

  initLookUpValues(): void {
    this.getListOfLookupValues(LookupValue.BusinessTypeByBroker, 'businessTypes');
    this.getListOfLookupValues(LookupValue.EntityStatus, 'entityStatuses');
    this.getListOfLookupValues(LookupValue.BrokerGroup, 'brokerGroups');
    this.getListOfLookupValues(LookupValue.UnderwritingType, 'underwritingConcessionsType');
    this.getListOfLookupValues(LookupValue.Feature, 'availableFeatures');
  }

  getListOfLookupValues(entity: string, propertyName: string): void {
    this.lookupService.getListOfLookupValues(entity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: NameValueDTO[]) => {
          this[propertyName] = response;
        },
        error: (error) => {
          this.sharedService.openErrorDialog(error.message);
        }
      });
  }

  onInputUpdatePageHeading(name: string) {
    if (this.isEditMode) {
      this.heading = this.getEditHeading(name);
    }
    else {
      this.heading = this.getAddHeading(name);
    }
  }

  getConcessionsTooltip(): string {
    if (!this.underwritingConcessions.length) {
      return;
    }

    return this.underwritingConcessions.map(c => {
      const startDate = c.startDate ? this.utilService.dateFormat(c.startDate) : '-';
      const endDate = c.endDate ? this.utilService.dateFormat(c.endDate) : '-';
      return `${c.underwritingType.name}:\n${startDate} to ${endDate}`;
    }).join('; ');
  }

  openUnderwritingConcessionsDialog() {
    const dialogData: UnderwritingConcessionDialogData = {
      underwritingTypes: this.underwritingConcessionsType,
      underwritingConcessionsToEdit: this.underwritingConcessions
    };

    this.dialog.open(UnderwritingConcessionDialogComponent, {
      data: dialogData,
      width: '45vw',
    }).afterClosed().subscribe(result => {
      if (result) {
        this.underwritingConcessions = result.map((concession: UnderwritingConcession) => ({
          underwritingType: concession.underwritingType,
          startDate: concession.startDate ? new Date(concession.startDate) : null,
          endDate: concession.endDate ? new Date(concession.endDate) : null,
          created: new Date(),
          createdBy: this.loggedInUserName,
          updated: this.isEditMode ? new Date() : null,
          updatedBy: this.isEditMode ? this.loggedInUserName : null,
          tenantId: null
        }));
      }
    });
  }

  onClickManagePermissions() {
    const dialogData: BrokerPermissionDialogData = {
      availableFeatures: this.availableFeatures,
      brokerFeatures: this.selectedFeatures,
      brokerId: this.brokerId,
      canManageFeatures: this.canManageFeatures
    };

    this.dialog.open(ManageBrokerPermissionsDialogComponent, {
      data: dialogData,
      width: '40vw'
    }).afterClosed().subscribe(result => {
      if (result) {
        this.selectedFeatures = result;
        this.calculatedNumberOfAllowedFeatures();
      }
    });
  }

  private getAddHeading(name: string) {
    return `${RouteHeadings.AddBroker} - ${name}`;
  }

  private getEditHeading(name: string) {
    return `${RouteHeadings.EditBroker} - ${name}`;
  }

  private navigateToBrokerPageBasedOnPortal() {
    const routes = {
      [PortalType.Normal]: {path: '/enterprise-management', queryParams: {tab: EnterpriseRoutesQueryParam.Brokers}},
      [PortalType.OnBoarding]: {path: '/onboarding/brokers', queryParams: {}},
      [PortalType.Broker]: {path: '/broker/brokers', queryParams: {}}
    };
    const route = routes[this.currentPortal] || routes[PortalType.Normal];
    this.router.navigate([route.path], {queryParams: route.queryParams});
  }

  private getBrokerDTOPayload(): BrokerDTO {
    // ----------------------------
    // Important:
    // Get Updated Payload from each step BEFORE creating Final Payload
    this.setPayloadFromEachStep();
    // ----------------------------

    const businessType = this.businessTypes.find(item => item.value === this.generalFormGroup.get('businessType')?.value);
    const entityStatus = this.entityStatuses.find(item => item.value === this.generalFormGroup.get('entityStatus')?.value);
    const brokerGroup = this.brokerGroups.find(item => item.value === this.generalFormGroup.get('brokerGroup')?.value);

    const brokerData: BrokerDTO = {
      brokerId: this.isEditMode ? this.brokerId : null,
      name: this.generalFormGroup.get('name')?.value,
      brokerNumber: this.generalFormGroup.get('brokerNumber')?.value,
      businessLicenseNumber: this.statutoryFormGroup.get('businessLicenseNumber')?.value,
      businessLicenseExpiryDate: this.statutoryFormGroup.get('businessLicenseExpiryDate')?.value,
      vatNumber: this.statutoryFormGroup.get('vatNumber')?.value,
      vatOveride: this.statutoryFormGroup.get('vatOverride')?.value ? 1 : 0,
      gstNumber: this.statutoryFormGroup.get('gstNumber')?.value,
      gstOverride: this.statutoryFormGroup.get('gstOverride')?.value ? 1 : 0,
      sanctionCheckDate: this.statutoryFormGroup.get('sanctionCheckDate')?.value,
      sanctionReference: this.statutoryFormGroup.get('sanctionReference')?.value,
      sanctionIndicator: this.statutoryFormGroup.get('sanctionIndicator')?.value ? 1 : 0,
      startDate: this.generalFormGroup.get('startDate')?.value,
      endDate: this.generalFormGroup.get('endDate')?.value,
      businessType,
      entityStatus,
      brokerGroup,
      underwritingConcessions: this.underwritingConcessions,
      addresses: this.addressPayload.addresses,
      contacts: this.contactPayload.contacts,
      created: this.isEditMode ? this.selectedBroker.created : new Date(),
      createdBy: this.isEditMode ? this.selectedBroker.createdBy : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null,
      features: this.selectedFeatures
    } as BrokerDTO;

    return brokerData;
  }

  private setPayloadFromEachStep(): void {
    this.addressPayload = this.addressFormComponent.getPayloadAsDTO();
    this.contactPayload = this.contactFormComponent.getPayloadAsDTO();
  }

  private getBrokerByBrokerId(brokerId: number): void {
    this.brokerService.getBrokerById(brokerId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (broker: BrokerDTO) => {
          this.selectedBroker = broker;
          this.selectedFeatures = this.selectedBroker.features;
          this.calculatedNumberOfAllowedFeatures();
          this.setFormsForEdit(broker);
          this.cdr.detectChanges();
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  private setFormsForEdit(data: BrokerDTO): void {
    // General Form
    this.generalFormGroup.patchValue({
      name: data.name,
      brokerNumber: data.brokerNumber,
      startDate: new Date(data.startDate),
      endDate: data.endDate ? new Date(data.endDate) : null,
      businessType: data.businessType?.value,
      entityStatus: data.entityStatus?.value,
      brokerGroup: data.brokerGroup?.value,
    });

    // Statutory Form
    this.statutoryFormGroup.patchValue({
      businessLicenseNumber: data.businessLicenseNumber,
      businessLicenseExpiryDate: new Date(data.businessLicenseExpiryDate),
      vatNumber: data.vatNumber,
      vatOverride: data.vatOveride === 1,
      gstNumber: data.gstNumber,
      gstOverride: data.gstOverride === 1,
      sanctionCheckDate: data.sanctionCheckDate ? new Date(data.sanctionCheckDate) : null,
      sanctionReference: data.sanctionReference,
      sanctionIndicator: data.sanctionIndicator === 1
    });

    // Addresses and Contacts
    this.selectedAddresses = data.addresses || [];
    this.selectedContacts = data.contacts || [];
    this.underwritingConcessions = data.underwritingConcessions || [];
    this.onInputUpdatePageHeading(data.name);
  }

  private getCustomRoute(): string {
    const routes = {
      [PortalType.Normal]: null,
      [PortalType.OnBoarding]: '/onboarding/brokers',
    };
    return routes[this.currentPortal] || null;
  }

  private calculatedNumberOfAllowedFeatures() {
    this.numberOfAllowedFeatures = this.selectedFeatures?.filter(x => x.accessLevel?.value == AccessLevel.Allow)?.length;
  }
}
